import React, { useEffect, useState } from 'react'
import { CheckCircle, Save, AlertCircle, RefreshCw } from 'lucide-react'
import { cn } from '../../lib/utils'

interface AutosaveIndicatorProps {
  lastSaved?: Date | string | null
  isSaving?: boolean
  hasError?: boolean
  className?: string
}

export const AutosaveIndicator: React.FC<AutosaveIndicatorProps> = ({
  lastSaved,
  isSaving = false,
  hasError = false,
  className
}) => {
  const [timeAgo, setTimeAgo] = useState<string>('')

  useEffect(() => {
    if (!lastSaved) return

    const updateTimeAgo = () => {
      const now = new Date()
      // Convert lastSaved to Date if it's a string (from localStorage)
      const savedDate = typeof lastSaved === 'string' ? new Date(lastSaved) : lastSaved
      
      // Check if the date is valid
      if (isNaN(savedDate.getTime())) {
        setTimeAgo('never')
        return
      }
      
      const diff = Math.floor((now.getTime() - savedDate.getTime()) / 1000)

      if (diff < 5) {
        setTimeAgo('just now')
      } else if (diff < 60) {
        setTimeAgo(`${diff} seconds ago`)
      } else if (diff < 3600) {
        const minutes = Math.floor(diff / 60)
        setTimeAgo(`${minutes} minute${minutes > 1 ? 's' : ''} ago`)
      } else {
        const hours = Math.floor(diff / 3600)
        setTimeAgo(`${hours} hour${hours > 1 ? 's' : ''} ago`)
      }
    }

    updateTimeAgo()
    const interval = setInterval(updateTimeAgo, 10000) // Update every 10 seconds

    return () => clearInterval(interval)
  }, [lastSaved])

  if (hasError) {
    return (
      <div className={cn(
        "flex items-center gap-2 text-sm text-red-600 animate-in fade-in duration-300",
        className
      )}>
        <AlertCircle className="w-4 h-4" />
        <span>Failed to save</span>
      </div>
    )
  }

  if (isSaving) {
    return (
      <div className={cn(
        "flex items-center gap-2 text-sm text-blue-600 animate-in fade-in duration-300",
        className
      )}>
        <RefreshCw className="w-4 h-4 animate-spin" />
        <span>Saving...</span>
      </div>
    )
  }

  if (lastSaved && timeAgo && timeAgo !== 'never') {
    return (
      <div className={cn(
        "flex items-center gap-2 text-sm text-green-600 animate-in fade-in duration-300",
        className
      )}>
        <CheckCircle className="w-4 h-4" />
        <span>Saved {timeAgo}</span>
      </div>
    )
  }

  return (
    <div className={cn(
      "flex items-center gap-2 text-sm text-gray-500",
      className
    )}>
      <Save className="w-4 h-4" />
      <span>Not saved yet</span>
    </div>
  )
}